import {
  asyncDataLoaderFeature,
  buildProxiedInstance,
  createOnDropHandler,
  dragAndDropFeature,
  type FeatureImplementation,
  hotkeysCoreFeature,
  type ItemInstance,
  keyboardDragAndDropFeature,
  propMemoizationFeature,
  selectionFeature,
  syncDataLoaderFeature,
  type TreeConfig,
  type TreeInstance,
} from '@headless-tree/core';
import { AssistiveTreeDescription, useTree } from '@headless-tree/react';
import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import { ChevronRightIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { navRowVariants } from './nav-row';
import { Spinner } from './spinner';

/**
 * Tree（层级列表）。逻辑底座是 `@headless-tree`——Base UI 没有 Tree primitive
 * （上游提案 mui/base-ui#3327 无排期），按「组件来源分层原则」第 2 级引成熟
 * headless 库、本文件只做样式包装（见 16 与 .cursor/rules/ui-component-sourcing.mdc）。
 *
 * 库被包在 `useTreeView` 后面，消费方不直接 import headless-tree，日后换底座只改本文件。
 * a11y（role/aria/键盘）由库按 W3C APG treeview 模式产出，我们不自己实现。
 */

const DEFAULT_INDENT = 20;
const DEFAULT_ITEM_SIZE = 28;
const DEFAULT_OVERSCAN = 8;

type VirtualizedOptions = {
  /** 行高估值（px）。行高固定时给准确值即可，可变高度由实测校正。 */
  estimateItemSize?: number;
  overscan?: number;
};

interface UseTreeViewOptions<T> extends Omit<
  TreeConfig<T>,
  'features' | 'instanceBuilder'
> {
  /**
   * 缩进（px）的单一真源：同时喂给渲染的视觉缩进与库的拖拽落点层级计算，
   * 避免「拖拽落点与看到的缩进对不上」。
   */
  indent?: number;
  /** 数据源为异步（换用 asyncDataLoaderFeature，配 `createLoadingItemData` 更佳）。 */
  async?: boolean;
  /** 开启拖拽；传了 `onDrop`/`onDropForeignDragObject` 时自动开启。 */
  dnd?: boolean;
  /** 开启虚拟化（内部换用 proxy 实例 + prop memoization）。 */
  virtualized?: boolean | VirtualizedOptions;
  /** 追加自定义 feature（如覆盖点击行为）。 */
  extraFeatures?: FeatureImplementation[];
}

interface TreeVirtual {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
}

interface TreeViewInstance<T> {
  /** 原始 headless-tree 实例，供本库未覆盖的高级用法。 */
  tree: TreeInstance<T>;
  /** 当前可见（已展开）节点的扁平列表。 */
  items: ItemInstance<T>[];
  indent: number;
  dnd: boolean;
  virtual: TreeVirtual | null;
}

/**
 * 按选项组装 features。headless-tree 的 features 需显式导入，漏了对应方法会在运行时
 * 静默失效而 TS 类型照样齐全——集中在这里装配即可避免。
 */
function buildTreeFeatures(options: {
  async?: boolean;
  dnd?: boolean;
  memoize?: boolean;
  extraFeatures?: FeatureImplementation[];
}): FeatureImplementation[] {
  const features: FeatureImplementation[] = [
    options.async ? asyncDataLoaderFeature : syncDataLoaderFeature,
    selectionFeature,
    hotkeysCoreFeature,
  ];
  if (options.dnd) {
    // 键盘拖拽是可达性要求，与鼠标拖拽成对启用。
    features.push(dragAndDropFeature, keyboardDragAndDropFeature);
  }
  if (options.memoize) features.push(propMemoizationFeature);
  if (options.extraFeatures?.length) features.push(...options.extraFeatures);
  return features;
}

function useTreeView<T>(options: UseTreeViewOptions<T>): TreeViewInstance<T> {
  const {
    indent = DEFAULT_INDENT,
    async: isAsync,
    dnd,
    virtualized,
    extraFeatures,
    ...config
  } = options;

  const dndEnabled = Boolean(
    dnd ?? (config.onDrop || config.onDropForeignDragObject),
  );
  // 拆成基本类型再进依赖数组：`virtualized` 常以对象字面量传入，直接依赖它会让
  // features 每次渲染都重建，进而抖动 useTree 的配置。
  const isVirtual = Boolean(virtualized);
  const virtualSettings =
    typeof virtualized === 'object' && virtualized !== null
      ? virtualized
      : null;
  const estimateItemSize =
    virtualSettings?.estimateItemSize ?? DEFAULT_ITEM_SIZE;
  const overscan = virtualSettings?.overscan ?? DEFAULT_OVERSCAN;

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const virtualizerRef = React.useRef<Virtualizer<
    HTMLDivElement,
    Element
  > | null>(null);

  const features = React.useMemo(
    () =>
      buildTreeFeatures({
        async: isAsync,
        dnd: dndEnabled,
        memoize: isVirtual,
        extraFeatures,
      }),
    [isAsync, dndEnabled, isVirtual, extraFeatures],
  );

  const tree = useTree<T>({
    ...config,
    indent,
    features,
    // 虚拟化下未挂载的行没有 DOM，滚动只能交给 virtualizer（官方要求同时换 proxy 实例）。
    ...(isVirtual
      ? {
          instanceBuilder: buildProxiedInstance,
          scrollToItem:
            config.scrollToItem ??
            ((item: ItemInstance<T>) =>
              virtualizerRef.current?.scrollToIndex(item.getItemMeta().index)),
        }
      : {}),
  });

  const items = tree.getItems();
  const estimateSize = React.useCallback(
    () => estimateItemSize,
    [estimateItemSize],
  );

  // hooks 不能条件调用，故非虚拟化时也创建（滚动元素为 null，virtualizer 空转不测量）。
  const virtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: isVirtual ? items.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan,
  });

  React.useEffect(() => {
    virtualizerRef.current = virtualizer;
  }, [virtualizer]);

  return {
    tree,
    items,
    indent,
    dnd: dndEnabled,
    virtual: isVirtual ? { scrollRef, virtualizer } : null,
  };
}

const TreeViewContext = React.createContext<TreeViewInstance<unknown> | null>(
  null,
);

function useTreeViewContext(part: string): TreeViewInstance<unknown> {
  const view = React.useContext(TreeViewContext);
  if (!view) throw new Error(`<${part}> must be rendered inside <Tree>`);
  return view;
}

type TreeRowContextValue = {
  style?: React.CSSProperties;
  index?: number;
  measureElement?: (element: HTMLElement | null) => void;
};

const TreeRowContext = React.createContext<TreeRowContextValue | null>(null);

const TreeItemContext = React.createContext<ItemInstance<unknown> | null>(null);

function useTreeItem(part: string): ItemInstance<unknown> {
  const item = React.useContext(TreeItemContext);
  if (!item) throw new Error(`<${part}> must be rendered inside <TreeItem>`);
  return item;
}

interface TreeProps<T> extends Omit<
  React.ComponentProps<'div'>,
  'children' | 'style'
> {
  view: TreeViewInstance<T>;
  /** 树的可读名，作为容器 `aria-label`（保持 i18n 无关，由调用方传本地化文本）。 */
  label?: string;
  /** 覆盖键盘拖拽的屏幕阅读器播报文案（默认英文）。 */
  getAssistiveLabel?: React.ComponentProps<
    typeof AssistiveTreeDescription
  >['getLabel'];
  /** 渲染单行。虚拟化开关对这里透明——只渲染可见行时也用同一个函数。 */
  children: (item: ItemInstance<T>) => React.ReactNode;
  style?: React.CSSProperties;
}

function Tree<T>({
  view,
  label,
  getAssistiveLabel,
  className,
  children,
  style,
  ...props
}: TreeProps<T>) {
  const containerProps = view.tree.getContainerProps(label);
  const dragLine = view.dnd ? <TreeDragLine view={view} /> : null;
  const assistive = (
    <AssistiveTreeDescription tree={view.tree} getLabel={getAssistiveLabel} />
  );

  // 无论是否虚拟化都提供同一个 context，使 TreeItem 的写法一致。
  const provider = (node: React.ReactNode) => (
    <TreeViewContext.Provider value={view as TreeViewInstance<unknown>}>
      {node}
    </TreeViewContext.Provider>
  );

  if (view.virtual) {
    const { scrollRef, virtualizer } = view.virtual;
    return provider(
      <div
        ref={scrollRef}
        data-slot="tree-scroll"
        className={cn('relative overflow-auto', className)}
        style={style}
        {...props}
      >
        <div
          {...containerProps}
          data-slot="tree"
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {assistive}
          {virtualizer.getVirtualItems().map((row) => {
            const item = view.items[row.index];
            if (!item) return null;
            return (
              <TreeRowContext.Provider
                key={item.getId()}
                value={{
                  index: row.index,
                  measureElement: virtualizer.measureElement,
                  style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${row.start}px)`,
                  },
                }}
              >
                {children(item)}
              </TreeRowContext.Provider>
            );
          })}
          {dragLine}
        </div>
      </div>,
    );
  }

  return provider(
    <div
      {...containerProps}
      data-slot="tree"
      className={cn('relative flex flex-col', className)}
      style={style}
      {...props}
    >
      {assistive}
      {view.items.map((item) => (
        <React.Fragment key={item.getId()}>{children(item)}</React.Fragment>
      ))}
      {dragLine}
    </div>,
  );
}

interface TreeItemProps<T> extends React.ComponentProps<'button'> {
  item: ItemInstance<T>;
  size?: 'sm' | 'md';
}

function TreeItem<T>({
  item,
  size,
  className,
  children,
  style,
  ...props
}: TreeItemProps<T>) {
  const view = useTreeViewContext('TreeItem');
  const row = React.useContext(TreeRowContext);
  const { ref: itemRef, ...itemProps } = item.getProps() as {
    ref?: (element: HTMLElement | null) => void;
  } & Record<string, unknown>;

  const setRef = React.useCallback(
    (element: HTMLButtonElement | null) => {
      itemRef?.(element);
      row?.measureElement?.(element);
    },
    [itemRef, row],
  );

  return (
    <TreeItemContext.Provider value={item as ItemInstance<unknown>}>
      <button
        {...itemProps}
        {...props}
        ref={setRef}
        data-slot="tree-item"
        data-index={row?.index}
        data-active={item.isSelected() || undefined}
        data-focused={item.isFocused() || undefined}
        // 未启用拖拽时 dragAndDropFeature 没装，调 isDragTarget 会被库直接抛错。
        data-drop={(view.dnd && item.isDragTarget()) || undefined}
        style={{ ...row?.style, ...style }}
        className={cn(navRowVariants({ size }), className)}
      >
        <span
          aria-hidden
          className="shrink-0"
          style={{ width: item.getItemMeta().level * view.indent }}
        />
        {children}
      </button>
    </TreeItemContext.Provider>
  );
}

/**
 * 展开箭头。非 folder 渲染等宽占位以保持标签对齐。纯指示器，展开由行本身的
 * 点击/键盘处理（`item.getProps()` 已带）触发。
 */
function TreeItemExpandIcon({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  const item = useTreeItem('TreeItemExpandIcon');
  if (!item.isFolder()) {
    return <span aria-hidden className="size-4 shrink-0" />;
  }
  return (
    <span
      aria-hidden
      data-slot="tree-item-expand-icon"
      data-expanded={item.isExpanded() || undefined}
      className={cn(
        'text-muted-foreground flex size-4 shrink-0 items-center justify-center transition-transform data-expanded:rotate-90',
        className,
      )}
      {...props}
    >
      <ChevronRightIcon className="size-3.5" />
    </span>
  );
}

function TreeItemLabel({ className, ...props }: React.ComponentProps<'span'>) {
  const item = useTreeItem('TreeItemLabel');
  return (
    <span
      data-slot="tree-item-label"
      className={cn('flex min-w-0 items-center gap-1.5 truncate', className)}
      {...props}
    >
      {props.children ?? item.getItemName()}
      {item.isLoading() ? (
        <Spinner className="text-muted-foreground size-3" />
      ) : null}
    </span>
  );
}

/** 拖拽插入位置指示线。`Tree` 在开启拖拽时自动渲染，也可自行摆放。 */
function TreeDragLine<T>({
  view,
  className,
  ...props
}: React.ComponentProps<'div'> & { view?: TreeViewInstance<T> }) {
  const fallback = React.useContext(TreeViewContext);
  const resolved = view ?? fallback;
  if (!resolved) throw new Error('<TreeDragLine> must be used inside <Tree>');
  return (
    <div
      aria-hidden
      data-slot="tree-drag-line"
      style={resolved.tree.getDragLineStyle()}
      className={cn(
        'bg-primary pointer-events-none absolute -mt-px h-0.5 rounded-full',
        className,
      )}
      {...props}
    />
  );
}

/** `<TreeView>` 的最简节点形状：一张 id → 节点的扁平表，`children` 存子节点 id。 */
interface TreeViewNode {
  name: string;
  children?: string[];
}

interface TreeViewProps<T extends TreeViewNode> extends Omit<
  React.ComponentProps<'div'>,
  'children' | 'onSelect'
> {
  items: Record<string, T>;
  rootId: string;
  /** 树的可读名，作为 `aria-label`（传本地化文本）。 */
  label?: string;
  size?: 'sm' | 'md';
  indent?: number;
  defaultExpandedIds?: string[];
  defaultSelectedIds?: string[];
  /** 传了即由本组件托管选中态并回传；不传则由库内部自管。 */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** 行的主动作（单击 / Enter）。 */
  onActivate?: (item: ItemInstance<T>) => void;
  /** 传了即开启拖拽排序；回调里把新的 children 顺序写回你的数据源。 */
  onReorder?: (parentId: string, childIds: string[]) => void;
  virtualized?: boolean | VirtualizedOptions;
  /** 自定义行内容（默认是展开箭头 + 名称）。 */
  renderItem?: (item: ItemInstance<T>) => React.ReactNode;
}

/**
 * 数据驱动的高层壳，覆盖大多数场景（同步数据源 + 可选拖拽排序/虚拟化）。
 * 需要异步懒加载、外部拖拽互操作等更细的控制时，改用 `useTreeView` + 复合部件。
 */
function TreeView<T extends TreeViewNode>({
  items,
  rootId,
  label,
  size,
  indent,
  defaultExpandedIds,
  defaultSelectedIds,
  onSelectionChange,
  onActivate,
  onReorder,
  virtualized,
  renderItem,
  ...props
}: TreeViewProps<T>) {
  const [selectedItems, setSelectedItems] = React.useState<string[]>(
    defaultSelectedIds ?? [],
  );
  const managed = Boolean(onSelectionChange);

  const view = useTreeView<T>({
    rootItemId: rootId,
    // 数据源里可能没有某个 id（如脏数据），运行时会是 undefined，故显式兜底。
    getItemName: (item) =>
      (item.getItemData() as T | undefined)?.name ?? item.getId(),
    isItemFolder: (item) =>
      Boolean((item.getItemData() as T | undefined)?.children),
    dataLoader: {
      getItem: (itemId) => items[itemId],
      getChildren: (itemId) => items[itemId]?.children ?? [],
    },
    initialState: {
      expandedItems: defaultExpandedIds ?? [],
      ...(managed ? {} : { selectedItems: defaultSelectedIds ?? [] }),
    },
    indent,
    virtualized,
    onPrimaryAction: onActivate,
    canReorder: Boolean(onReorder),
    dnd: Boolean(onReorder),
    onDrop: onReorder
      ? createOnDropHandler<T>((item, newChildren) =>
          onReorder(item.getId(), newChildren),
        )
      : undefined,
    ...(managed
      ? {
          state: { selectedItems },
          setSelectedItems: (updater) => {
            const next =
              typeof updater === 'function' ? updater(selectedItems) : updater;
            setSelectedItems(next);
            onSelectionChange?.(next);
          },
        }
      : {}),
  });

  return (
    <Tree view={view} label={label} {...props}>
      {(item) => (
        <TreeItem item={item} size={size}>
          {renderItem ? (
            renderItem(item)
          ) : (
            <>
              <TreeItemExpandIcon />
              <TreeItemLabel />
            </>
          )}
        </TreeItem>
      )}
    </Tree>
  );
}

/**
 * 单个节点的实例（headless-tree 的 `ItemInstance`），透传给渲染函数与回调。
 * 从本包再导出一次，使消费方不必直接依赖 headless-tree。
 */
type TreeItemInstance<T> = ItemInstance<T>;

export type {
  TreeItemInstance,
  TreeViewInstance,
  TreeViewNode,
  TreeViewProps,
  TreeVirtual,
  VirtualizedOptions as TreeVirtualizedOptions,
  UseTreeViewOptions,
};
export {
  buildTreeFeatures,
  createOnDropHandler,
  Tree,
  TreeDragLine,
  TreeItem,
  TreeItemExpandIcon,
  TreeItemLabel,
  TreeView,
  useTreeView,
};
